/**
 * Mirror of the Loupe matcher in
 * packages/pipelines/loupe/src/episode/transcriptFingerprints.ts — keep the
 * thresholds in sync so the case view shows the lines analyze will tag.
 */

export const MIN_TOKENS = 6;
const MIN_COVERAGE = 0.8;

function spanSlack(readTokens: number): number {
  return Math.min(8, Math.max(2, Math.floor(readTokens / 3)));
}

function fold(text: string): string {
  return text
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[''`´]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim()
    .replace(/\s+/g, " ");
}

function tokens(text: string): string[] {
  const folded = fold(text);
  return folded ? folded.split(" ") : [];
}

function hasBrand(lineTokens: readonly string[], names: readonly string[]): boolean {
  const joined = ` ${lineTokens.join(" ")} `;
  const compact = lineTokens.join("");
  return names.some((name) => {
    const key = fold(name);
    if (!key) return false;
    if (joined.includes(` ${key} `)) return true;
    const compactKey = key.replace(/ /g, "");
    return compactKey.length >= 6 && compact.includes(compactKey);
  });
}

function matchSpan(
  line: readonly string[],
  read: readonly string[],
): { start: number; end: number } | null {
  const n = read.length;
  const m = line.length;
  if (n < MIN_TOKENS || m === 0) return null;
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = 1; i <= n; i += 1) {
    for (let j = 1; j <= m; j += 1) {
      dp[i][j] =
        read[i - 1] === line[j - 1]
          ? dp[i - 1][j - 1] + 1
          : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }
  if (dp[n][m] < Math.ceil(n * MIN_COVERAGE)) return null;
  let i = n;
  let j = m;
  let first = -1;
  let last = -1;
  while (i > 0 && j > 0) {
    if (read[i - 1] === line[j - 1]) {
      if (last < 0) last = j - 1;
      first = j - 1;
      i -= 1;
      j -= 1;
    } else if (dp[i - 1][j] >= dp[i][j - 1]) {
      i -= 1;
    } else {
      j -= 1;
    }
  }
  if (first < 0 || last - first + 1 > n + spanSlack(n)) return null;
  return { start: first, end: last };
}

/** First line where `read` starts. The read may continue into the next lines. */
function matchReadFromLine(
  lineTokens: readonly (readonly string[])[],
  read: readonly string[],
  fromLine: number,
): number | null {
  const n = read.length;
  const slack = spanSlack(n);
  for (let start = fromLine; start < lineTokens.length; start += 1) {
    const first = lineTokens[start] ?? [];
    if (first.length === 0) continue;
    const window: string[] = [];
    let total = 0;
    const need = first.length + n + slack - 1;
    for (let k = start; k < lineTokens.length && total < need; k += 1) {
      const part = lineTokens[k] ?? [];
      window.push(...part);
      total += part.length;
    }
    const across = matchSpan(window, read);
    const span = across && across.start < first.length ? across : matchSpan(first, read);
    if (span && span.start < first.length) return start;
  }
  return null;
}

/**
 * Indexes of lines a saved read would tag. The read may span several lines
 * (analyze splits a Whisper segment into sentences). A line that already says
 * the brand is left untagged.
 */
export function fingerprintTaggedLines(
  lines: readonly { text: string }[],
  reads: readonly string[],
  brandNames: readonly string[],
): Set<number> {
  const readTokens = reads.map(tokens).filter((t) => t.length >= MIN_TOKENS);
  const tagged = new Set<number>();
  if (!readTokens.length) return tagged;
  const lineTokens = lines.map((line) => tokens(line.text));
  for (const read of readTokens) {
    let from = 0;
    while (from < lines.length) {
      const hit = matchReadFromLine(lineTokens, read, from);
      if (hit == null) break;
      const startTokens = lineTokens[hit] ?? [];
      if (!hasBrand(startTokens, brandNames)) tagged.add(hit);
      from = hit + 1;
    }
  }
  return tagged;
}
